/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { For, onCleanup, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { SiteDomain } from '../../../types/app-state';
import {
    StyledBottomSection,
    StyledButtonsContainer,
    StyledButtonWrapper,
    StyledEmptyColumnPlaceholder,
    StyledlanguageSelectorOverlay,
    StyledSidePanel,
} from './language-selector.style';
import { Text } from '../../../ui-components/text/text';
import theme from '../../../style/theme';
import { Grid } from '../../../grid-system/grid/grid';
import { Heading } from '../../../ui-components/heading/heading';
import { Button } from '../../../ui-components/button/button';
import { CrossIcon } from '../../icons-library/cross';
import { setLanguage } from '../../../stores/locale-store';
import { containerQueries } from '../../../tools/check-container-support';

const languageCodes: { [key: string] : string} = { // TODO: move someplace better, e.g. Atos globals
    'nl': 'Nederlands',
    'en': 'English',
    'fr': 'Français',
    'de': 'Deutsch',
    'es': 'Español',
    'it': 'Italiano',
    'pt': 'Português',
    'fi': 'Suomi',
    'sv': 'Svenska',
};

type LanguageSelectorProps = {
    siteLanguages: { current: SiteDomain; countrySites: SiteDomain[]};
    openCountrySelection: () => void;
    internationalSite: SiteDomain | undefined;
}

export const LanguageSelector = (props: LanguageSelectorProps) => {
    const { localize, viewport, siteInfo, updateLanguage } = useContext(AppContext);
    let styledSidePanelRef: HTMLDivElement | undefined;

    onMount(() => {
        const body = document.getElementsByTagName('body')[0];
        body.style.overflow = 'hidden';
    });

    onCleanup(() => {
        const body = document.getElementsByTagName('body')[0];
        body.style.overflow = 'auto';
    });

    const isTabletSmall = () => viewport.width <= theme.breakpoints.TABLET_SMALL;

    const languageLabel = (key: string | undefined) => {
        if (!key) {
            return '';
        }
        const shortkey = key.substring(0, 2);
        return languageCodes[shortkey];
    };

    const handleOverlayClick = (e: MouseEvent) => {        
        if (!styledSidePanelRef?.contains(e.target as Node)) {
            const languageProps = {
                site: props.siteLanguages.current,
                isCurrent: true,
                siteInfo,
                updateLanguage: (lang: string) => updateLanguage(lang),
            };
            setLanguage(languageProps);
            return;
        }
    };

    return (
        <StyledlanguageSelectorOverlay onClick={handleOverlayClick}>
            <Grid
                templateShorthand={[12]}
                isLayoutGrid={true}
                autoResponsiveness={true}
                customCss={`
                    height: 100%;
                    ${containerQueries('mobileMax')} {
                        background: ${theme.palette.white};
                        height: 85%;
                        border-radius: 0 0 3rem 3rem;
                    }
                `}
            >
                <Grid
                    templateShorthand={[7,5]}
                    responsive={{ 
                        tablet: isTabletSmall() ?  [3,9] : [4,8],
                        mobile: [12],
                    }}
                    customCss={`
                        height: 100%;
                        ${containerQueries('mobileMax')} {
                            height: 80%;
                        }
                    `}
                >

                    <StyledEmptyColumnPlaceholder />
                    <StyledSidePanel ref={styledSidePanelRef}>
                        <div style={{ 'display': 'flex', 'justify-content': 'flex-end', 'margin-bottom': '3rem'}}>
                            <CrossIcon 
                                fill="gray"
                                width={1.79}
                                height={1.79}
                                onClick={() => setLanguage({
                                    site: props.siteLanguages.current,
                                    isCurrent: true,
                                    siteInfo,
                                    updateLanguage: (lang) => updateLanguage(lang),
                                })}
                            />
                        </div>


                        <Heading tag='h2' noBlockSpacing={true} variant='xlarge'>
                            {`${localize('welcome-to', 'Welcome to')}`}
                        </Heading>
                        <Heading tag='h2' variant='xlarge'>
                            {`${props.siteLanguages?.current.siteName}.`}
                        </Heading>

                        <Text fontSize='normal' fontStyle='bold'>
                            {localize('please-select-your-language', 'Please select your language')}
                        </Text>
                        <StyledButtonsContainer>
                            <Show when={props.siteLanguages?.current}>
                                <StyledButtonWrapper>
                                    <Button 
                                        label={languageLabel(props.siteLanguages?.current.key)}
                                        onClick={() => setLanguage({
                                            site: props.siteLanguages.current,
                                            isCurrent: true,
                                            siteInfo,
                                            updateLanguage: (lang) => updateLanguage(lang),
                                        })}
                                    />
                                </StyledButtonWrapper>
                            </Show>
                            
                            <For each={props.siteLanguages?.countrySites}>{(site: SiteDomain) =>
                                <StyledButtonWrapper>
                                    <Button
                                        label={languageLabel(site.key)}
                                        onClick={() => setLanguage({
                                            site,
                                            isCurrent: false,
                                            siteInfo,
                                            updateLanguage: (lang) => updateLanguage(lang)
                                        })}
                                    />
                                </StyledButtonWrapper>
                            }</For>
                        </StyledButtonsContainer>

                        <StyledBottomSection>
                            <Show when={props.internationalSite}>
                                <Button
                                    label={`${localize('atos-medical-global', 'Atos Medical Global')}`}
                                    onClick={() => setLanguage({
                                        site: props.internationalSite!,
                                        isCurrent: false,
                                        siteInfo, 
                                        updateLanguage: (lang) => updateLanguage(lang),
                                    })}
                                    variant='secondary'
                                />                        
                            </Show>

                            <div>
                                <Button
                                    label={localize('choose-another-country', 'Choose another country')}
                                    variant='tertiary'
                                    arrow={true}
                                    onClick={() => props.openCountrySelection()}
                                />
                            </div>
                        </StyledBottomSection>
                    </StyledSidePanel>
                </Grid>
            </Grid>
        </StyledlanguageSelectorOverlay>
    );
};